import React, { useCallback, useMemo } from 'react';
import { Drawer } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import { DateTime } from 'luxon';
import { FilterEmploymentType } from 'shared/components/filters/inputs/employment-type/filter-employment-type';
import { getShiftPlanningFiltersDefaultValues } from './utils/get-shift-planning-default-values';
import {
  ShiftPlanningFiltersFormType,
  ShiftPlanningFiltersProps,
} from './shift-planning-filters.types';
import { FilterWrapper } from '../../../../../../shared/components/filters/filter-wrapper';
import { FilterFromDate } from '../../../../../../shared/components/filters/inputs/date/filter-from-date';
import { FilterBusinessUnitsWithCompany } from '../../../../../../shared/components/filters/inputs/business-units-with-company/filter-business-units-with-company';
import { FilterFilledStatus } from '../../../../../../shared/components/filters/inputs/filled-status/filter-filled-status';
import { FilterJobTypes } from '../../../../../../shared/components/filters/inputs/job-types/filter-job-types';
import { FilterApplicantStatus } from '../../../../../../shared/components/forms/inputs/filter-applicant-status/filter-applicant-status';

export const ShiftPlanningFilters = ({
  toggleFilters,
  handleFiltersChange,
  appliedFilters,
  isOpen,
  filterData,
}: ShiftPlanningFiltersProps) => {
  const defaultFilterValues = useMemo(() => {
    return getShiftPlanningFiltersDefaultValues(appliedFilters, filterData);
  }, [appliedFilters, filterData]);

  const form = useForm<ShiftPlanningFiltersFormType>({ defaultValues: { ...defaultFilterValues } });

  const handleClear = useCallback(() => {
    form.reset({
      fromDate: defaultFilterValues.fromDate,
      fromDateCheckboxValue: false,
      businessUnitCheckboxValue: false,
      company: null,
      branchOffices: null,
      departments: null,
      filledStatusCheckboxValue: false,
      filledStatus: null,
      jobTypeCheckboxValue: false,
      jobType: [],
      employmentTypeCheckboxValue: false,
      isFreelance: false,
      isTempWork: false,
      isEmployee: false,
      applicationTypesCheckboxValue: false,
      areFreelanceApplications: false,
      areTempworkApplications: false,
      areNoApplications: false,
    });
  }, [defaultFilterValues, form]);

  const handleCancel = () => {
    toggleFilters();
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      form.handleSubmit((data) => {
        const branchOffices = data.branchOffices?.map((item) => item.value) ?? undefined;
        const departments = data.departments?.map((item) => item.value) ?? undefined;
        const dataToSubmit = {
          fromDate:
            data.fromDateCheckboxValue && data.fromDate
              ? DateTime.fromFormat(data.fromDate, 'yyyy-MM-dd')
              : undefined,
          company: data.businessUnitCheckboxValue ? data.company?.value : undefined,
          branchOffices: data.businessUnitCheckboxValue ? branchOffices : undefined,
          departments: data.businessUnitCheckboxValue ? departments : undefined,
          filledStatus:
            data.filledStatusCheckboxValue && data.filledStatus !== null
              ? data.filledStatus
              : undefined,
          jobType:
            data.jobTypeCheckboxValue && data.jobType?.length
              ? data.jobType.map((jobTypeSingle) => jobTypeSingle.id)
              : undefined,
          isFreelance: data.employmentTypeCheckboxValue ? data.isFreelance : undefined,
          isTempWork: data.employmentTypeCheckboxValue ? data.isTempWork : undefined,
          isEmployee: data.employmentTypeCheckboxValue ? data.isEmployee : undefined,
          areFreelanceApplications: data.applicationTypesCheckboxValue
            ? data.areFreelanceApplications
            : undefined,
          areTempworkApplications: data.applicationTypesCheckboxValue
            ? data.areTempworkApplications
            : undefined,
          areNoApplications: data.applicationTypesCheckboxValue
            ? data.areNoApplications
            : undefined,
        };

        handleFiltersChange(dataToSubmit);
        toggleFilters();
      })();
    },
    [form, handleFiltersChange, toggleFilters]
  );

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleCancel}>
      <FormProvider {...form}>
        <FilterWrapper
          filterName="Filter"
          onCancel={handleCancel}
          onClear={handleClear}
          onSubmit={handleSubmit}
        >
          <FilterFromDate />
          <FilterBusinessUnitsWithCompany companies={filterData?.companies ?? []} />
          <FilterApplicantStatus />
          <FilterFilledStatus />
          <FilterJobTypes jobTypes={filterData.jobTypes} />
          <FilterEmploymentType hasEmployee={true} />
        </FilterWrapper>
      </FormProvider>
    </Drawer>
  );
};
